import { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useRouter } from 'shared-routing';
import { DiningReservationTemplate } from 'voyager';
import type { CalendarView, DiningReservationRestaurant } from 'voyager';

import { BasketDrawerVersion } from '@common/components/BasketDrawer';
import { Routes } from '@common/constants';
import { useGetRestaurantConfig } from '@features/tables/hooks/useGetRestaurantConfig';
import { DEFAULT_MAX_PARTY_SIZE } from '@features/tables/constants';
import { findBookingRestaurant } from '@features/tables/bookingRoute';
import { getDiningCovers } from '@features/tables/diningParty';
import { useTranslations } from '@lang';
import { yourJourneySelectors } from '@store/yourJourney';
import type { ParsedJourneyParams } from '@store/yourJourney/selectors';
import { getWeekMonthStart } from '@utils/date';
import rewriteHttp from '@utils/rewriteHttp';

import type { DiningReservationV2Props } from './DiningReservationV2.types';
import { useDiningPreferences } from './useDiningPreferences';
import { useReservationBasketId } from '@common/hooks';
import { formatIsoDate, useDiningRestaurants, useRestaurantWeekAvailability } from './queries';

const DEFAULT_CURRENCY = 'GBP';

function isoDaysFromToday(days = 0) {
  const date = new Date();
  date.setDate(date.getDate() + days);
  return formatIsoDate(date);
}

export const DiningReservationV2: FC<DiningReservationV2Props> = ({
  restaurantId,
  property,
  basketDetails,
  language,
  createBasket,
  updateBasket,
  fetchBasketDetails,
}) => {
  const { t } = useTranslations();
  const router = useRouter();
  const journey = useSelector(yourJourneySelectors.getParsedJourneyParams) as ParsedJourneyParams;
  const rawFromDate = useSelector(yourJourneySelectors.getActualFromDateValue);
  const searchedDate =
    rawFromDate && journey.fromDate?.isValid() ? journey.fromDate.format('YYYY-MM-DD') : undefined;
  const { basketId, basketNotReady } = useReservationBasketId({
    basketDetails,
    propertyId: property.id,
    language,
    fetchBasketDetails,
  });

  const { data: restaurantsData } = useDiningRestaurants();
  const restaurants = Array.isArray(restaurantsData) ? restaurantsData : [];

  const currency = property.defaultCurrency || DEFAULT_CURRENCY;

  const [selectedRestaurantId, setSelectedRestaurantId] = useState<string | undefined>(
    restaurantId
  );
  const [guestCount, setGuestCount] = useState(
    Math.min(getDiningCovers(journey), DEFAULT_MAX_PARTY_SIZE)
  );
  const [activeDate, setActiveDate] = useState(() => searchedDate ?? isoDaysFromToday());
  const [visibleMonth, setVisibleMonth] = useState<string | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<string | undefined>(undefined);
  const [selectedTimeslot, setSelectedTimeslot] = useState<string | undefined>(undefined);
  const [selectedPromotionId, setSelectedPromotionId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isBasketDrawerOpen, setIsBasketDrawerOpen] = useState(false);
  const submittingRef = useRef(false);

  const propertyModules = Object.keys(property.modulesExcludingDisabled || {});

  const selectedRestaurant = findBookingRestaurant(restaurants, selectedRestaurantId);

  const { availability, timeslots, promotions, isLoading } = useRestaurantWeekAvailability(
    selectedRestaurant,
    activeDate,
    guestCount,
    0
  );

  const restaurantName = selectedRestaurant?.restaurantName ?? '';
  const { data: restaurantConfig } = useGetRestaurantConfig(
    property.id,
    activeDate,
    restaurantName,
    Boolean(restaurantName)
  );
  const { data: todayRestaurantConfig } = useGetRestaurantConfig(
    property.id,
    isoDaysFromToday(),
    restaurantName,
    Boolean(restaurantName)
  );

  const slotMax =
    timeslots.reduce(
      (max, slot) => (slot.maxPartySize != null ? Math.max(max, slot.maxPartySize) : max),
      0
    ) || null;
  const slotMinRaw = timeslots.reduce(
    (min, slot) => (slot.minPartySize != null ? Math.min(min, slot.minPartySize) : min),
    Infinity
  );
  const slotMin = Number.isFinite(slotMinRaw) ? slotMinRaw : null;
  const maxPartySize = slotMax ?? restaurantConfig?.maxOnlinePartySize ?? DEFAULT_MAX_PARTY_SIZE;
  const minPartySize = slotMin ?? restaurantConfig?.minOnlinePartySize ?? 1;
  const maxGuests = Math.max(1, maxPartySize);
  const minGuests = Math.max(1, minPartySize);
  const bookingWindowDays = Number(todayRestaurantConfig?.acceptBookingsDaysInAdvance);
  const maxDate = bookingWindowDays > 0 ? isoDaysFromToday(bookingWindowDays) : undefined;
  const calendarView: CalendarView = visibleMonth ? 'month' : 'week';

  useEffect(() => {
    if (guestCount > maxGuests) {
      setGuestCount(maxGuests);
    } else if (guestCount < minGuests) {
      setGuestCount(minGuests);
    }
  }, [maxGuests, minGuests]);

  const dayTimeslots = useMemo(() => {
    const seenStartTimes = new Set<string>();
    return timeslots
      .filter((slot) => slot.date === selectedDate && slot.hasStandardAvailability)
      .filter((slot) => {
        if (seenStartTimes.has(slot.startTime)) return false;
        seenStartTimes.add(slot.startTime);
        return true;
      })
      .map((slot) => ({
        value: slot.startTime,
        label: slot.startTime,
      }));
  }, [timeslots, selectedDate]);

  const selectedSlot = timeslots.find(
    (slot) =>
      slot.date === selectedDate &&
      slot.startTime === selectedTimeslot &&
      slot.hasStandardAvailability
  );
  const depositAmount = Number(selectedSlot?.standardAvailabilityFeeAmount ?? 0);
  const depositMessage =
    depositAmount > 0
      ? t('dining.depositMessage', {
          amount: new Intl.NumberFormat(language, {
            style: 'currency',
            currency,
          }).format(depositAmount),
        })
      : undefined;

  const restaurantOptions: DiningReservationRestaurant[] = restaurants.map((restaurant) => ({
    id: restaurant.providerId,
    name: restaurant.name,
    subtitle: restaurant.subtitle ?? undefined,
    imageUrl: restaurant.imageUrls[0] ? rewriteHttp(restaurant.imageUrls[0]) : undefined,
  }));

  const menuOptions = useMemo(
    () =>
      (selectedSlot?.availablePromotions ?? []).flatMap((available) => {
        const promotion = promotions.find((entry) => entry.id === available.id);
        return promotion ? [{ value: promotion.id, label: promotion.name }] : [];
      }),
    [selectedSlot, promotions]
  );

  const selectedPromotion = selectedSlot?.availablePromotions.find(
    (available) => available.id === selectedPromotionId
  );

  const heroSource = selectedRestaurant?.imageUrls[0] ?? restaurants[0]?.imageUrls[0];
  const heroImage = heroSource ? rewriteHttp(heroSource) : undefined;

  const clearTimeslot = () => {
    setSelectedTimeslot(undefined);
    setSelectedPromotionId(undefined);
  };

  const selectTimeslot = (time: string) => {
    setSelectedTimeslot(time);
    setSelectedPromotionId(undefined);
  };

  const selectRestaurant = (id: string) => {
    setSelectedRestaurantId(id);
    setSelectedDate(undefined);
    clearTimeslot();
  };

  const preferences = useDiningPreferences();

  const handleReserve = async () => {
    if (!selectedRestaurant || !selectedSlot || submittingRef.current || basketNotReady) return;

    const tableItem = {
      type: 'tables',
      adults: guestCount,
      children: 0,
      note: preferences.values.note,
      dietaryRequirements: preferences.values.dietaryRequirements,
      celebrations: preferences.values.celebrations,
      bookable: {
        ids: {
          timeslot: selectedSlot.timeSlot,
          restaurant: selectedRestaurant.restaurantName,
          restaurantName: selectedRestaurant.configName,
          restaurantConfig: selectedRestaurant.restaurantId,
          promotionId: selectedPromotion?.id,
          timeslotReservationId:
            selectedPromotion?.timeslotReservationId ??
            selectedSlot.timeslotReservationId ??
            undefined,
        },
      },
    };

    submittingRef.current = true;
    setIsSubmitting(true);
    try {
      if (basketId) {
        const existingItems = Array.isArray(basketDetails?.items) ? basketDetails.items : [];
        await updateBasket(property.id, basketId, language, {
          items: [tableItem, ...existingItems],
        });
      } else {
        await createBasket(property.id, language, { items: [tableItem] });
      }

      setIsBasketDrawerOpen(true);
    } catch {
    } finally {
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <DiningReservationTemplate
        preferenceCategories={preferences.preferenceCategories}
        onPreferenceOptionToggle={preferences.onPreferenceOptionToggle}
        onPreferenceOtherChange={preferences.onPreferenceOtherChange}
        onPreferenceFreeTextChange={preferences.onPreferenceFreeTextChange}
        imageUrl={heroImage}
        imageAlt={selectedRestaurant?.name ?? t('app.dining')}
        onBack={() => router.push({ pathname: Routes.TABLES_HOME })}
        restaurants={restaurantOptions}
        selectedRestaurantId={selectedRestaurant?.providerId ?? selectedRestaurantId}
        onSelectRestaurant={selectRestaurant}
        guestCount={guestCount}
        minGuests={minGuests}
        maxGuests={maxGuests}
        onGuestCountChange={(count) => {
          setGuestCount(count);
          clearTimeslot();
        }}
        activeDate={visibleMonth ?? activeDate}
        selectedDate={selectedDate}
        calendarView={calendarView}
        availability={availability}
        maxDate={maxDate}
        onDateSelect={(date) => {
          setSelectedDate(date);
          clearTimeslot();
          setActiveDate(date);
          setVisibleMonth(undefined);
        }}
        onWeekNavigate={(date) => {
          if (visibleMonth) {
            setVisibleMonth(date);
            return;
          }
          setActiveDate(date);
          setSelectedDate(undefined);
          clearTimeslot();
        }}
        onCalendarViewChange={(view) =>
          setVisibleMonth(view === 'month' ? getWeekMonthStart(activeDate) : undefined)
        }
        timeslots={dayTimeslots}
        isLoading={isLoading}
        selectedTimeslot={selectedTimeslot}
        onTimeslotSelect={selectTimeslot}
        progressStep={selectedTimeslot ? 2 : 1}
        menuOptions={menuOptions}
        selectedMenuId={selectedPromotionId}
        onMenuSelect={setSelectedPromotionId}
        depositMessage={depositMessage}
        onReserve={handleReserve}
        reserveDisabled={isSubmitting || basketNotReady}
        labels={{
          heading: t('dining.reservationHeading'),
          subheading: t('dining.reservationSubheading'),
          restaurantTitle: t('dining.restaurantTitle'),
          restaurantSubtitle: t('dining.restaurantSubtitle'),
          reservationTitle: t('dining.reservationTitle'),
          reservationSubtitle: t('dining.reservationSubtitle'),
          guestLabel: t('dining.guests'),
          timeLabel: t('dining.time'),
          noTimeslotsLabel: t('dining.noTimeslots'),
          menuTitle: t('dining.menuTitle'),
          menuFieldLabel: t('dining.menuFieldLabel'),
          changeRestaurant: t('dining.changeRestaurant'),
          dismiss: t('dining.close'),
          reserveLabel: t('dining.reserve'),
          back: t('dining.back'),
          noImage: t('basket.noImageLabel'),
          calendar: {
            previousWeek: t('spa.booking.calendar.previousWeek'),
            nextWeek: t('spa.booking.calendar.nextWeek'),
            previousMonth: t('aria.previousMonth'),
            nextMonth: t('aria.nextMonth'),
            openCalendar: t('spa.booking.calendar.openCalendar'),
            closeCalendar: t('spa.booking.calendar.closeCalendar'),
          },
          preferences: {
            title: t('dining.preferencesTitle'),
            otherButtonLabel: t('dining.preferencesOther'),
          },
        }}
      />
      <BasketDrawerVersion
        open={isBasketDrawerOpen}
        onClose={() => setIsBasketDrawerOpen(false)}
        propertyModules={propertyModules}
      />
    </>
  );
};
